import { useMemo, useEffect, useState, useRef } from "react";
import axios from "axios";
import { CircularProgress, Pagination, Box } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import Readmore from "../Dashboard/Readmore";
import CustomdateC from "../Dashboard/CustomdateC";
import ExportL from "../Dashboard/ExportL";
import dayjs from 'dayjs';
import Question from "../Dashboard/Question";
import { ThumbsUp } from 'lucide-react'; 
import { API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import { getDomainId } from "utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "context/QueryParamsContext";
import { enUS } from "date-fns/locale";
import { useDomain } from "context/DomainContext";
import CustomdateD from "../Dashboard/CustomdateD";
import { useAuth } from "context/AuthContext";

function DataTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25],
  canSearch = false,
  date = false,
  csv = false,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  dashboardDateRange
}) {
  const columns = useMemo(() => table.columns, [table]);
  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const location = useLocation();
  const isFirstLoading = useRef(true);
  const [hoveredCountry, setHoveredCountry] = useState(null);
         const navigate= useNavigate();

  const { dateRange, setDateRange } = useQueryParams();

  useEffect(() => {
    if (dashboardDateRange?.start_date && dashboardDateRange?.end_date) {
      setDateRange(dashboardDateRange);
    }
  }, [dashboardDateRange, setDateRange]);

  const totalPages = totalEntries && entriesPerPage ? Math.ceil(totalEntries / entriesPerPage) : 1;
  const start = totalEntries === 0 ? 0 : (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries || 0);
      const { currentDomain } = useAuth();
      const domainId = currentDomain?.domain_token;
      //  const { domainId } = useDomain();
       if(!domainId){
        navigate("/Access-Denied")
       }

  const fetchUsers = async () => {
    if (isFirstLoading.current) setLoading(true);
    try {

      const { start_date, end_date } = dateRange;
      const offset = (page - 1) * entriesPerPage;
      const res = await axios.get(`${LAMBDA_BASE_URL}/likes`, {
        params: {
          limit: entriesPerPage,
          offset,
          search,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date,
        },
        headers: {
          'domain-token': domainId
        }
      });
     
      const formattedRows = res.data.result.search_list.map((user) => ({
        id: user.id,
        content: {
          question: <Question text={user.question} />,
          
        },
        ip_address: user.ip_address,
        created_at: dayjs(user.created_at).format('MMM D, YYYY hh:mm A'),
        answer: user.answer,
        likes: user.likes,
        
      }));

      setRows(formattedRows);
      setTotalEntries(res.data.result.total);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      if (isFirstLoading.current) {
        setLoading(false);
        isFirstLoading.current = false;
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, entriesPerPage, search, sortConfig, dateRange,domainId]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    localStorage.setItem('saved_start_date', start);
    localStorage.setItem('saved_end_date', end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('start_date', start);
    queryParams.set('end_date', end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  return (
    <TableContainer sx={{ boxShadow: "none", minHeight: "450px" }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <h4 style={{ textAlign: "left", paddingBottom: "0px",paddingLeft:"10px" }}><ThumbsUp color="green" size={16}/>&nbsp;Liked Answers</h4>
          {(entriesPerPageOptions || canSearch || date || csv) && (
        <SoftBox
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={2}
          p={2}
        >
          <SoftBox display="flex" alignItems="center" sx={{ width: { xs: "100%", md: "auto" } }}>
            <SoftSelect
              defaultValue={{ value: entriesPerPage, label: entriesPerPage }}
              options={entriesPerPageOptions.map((entry) => ({
                value: entry,
                label: entry,
              }))}
              onChange={handleEntriesPerPageChange}
              size="small"
            />
            <SoftTypography variant="caption" color="secondary" ml={1}>
              entries per page
            </SoftTypography>
          </SoftBox>
          <SoftBox
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
            width={{ xs: "100%", md: "auto" }}
          >    
            {canSearch && (
              <SoftBox sx={{ width: { xs: "100%", md: "12rem" } }}>
                <SoftInput
                  placeholder="Search..."
                  value={search}
                  onChange={handleSearchChange}
                  fullWidth
                />
              </SoftBox>
            )} 
            {date && (
              <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                <CustomdateD onRangeSelect={handleDateRangeSelect} />
              </SoftBox>
            )}        
            {csv && (
              <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                <ExportL searchQuery={search} startDate={dateRange.start_date} endDate={dateRange.end_date} filename="ansera_like_details.csv" />
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
          )}
          <Table>
            <SoftBox component="thead">
              {columns.map((column, key) => {
                const isSortable = !["Content", "likes"].includes(column.Header);
                return (
                  <DataTableHeadCell
                    key={key}
                    width={column.width || "auto"}
                    align={column.align || "left"}
                    onClick={isSortable ? () => handleSort(column.accessor) : undefined}
                    sorted={isSortable && sortConfig.key === column.accessor ? sortConfig.direction : "none"}
                    showSortIcon={!["content", "likes"].includes(column.accessor)}
                    style={{ cursor: isSortable ? "pointer" : "default", userSelect: "none" }}
                  >
                    {column.Header}
                  </DataTableHeadCell>
                );
              })}
            </SoftBox>
            <TableBody>
              {rows.map((row, key) => (
                <TableRow key={key}>
                  {Object.entries(row).map(([colKey, cell], index) => {
                    if (colKey === "id") return null;

                    return (
                      <DataTableBodyCell
                        key={index}
                        align="left"
                        colSpan={colKey === "content" ? columns.length : 1}
                      >
                        {colKey === "content" ? (
                          <>
                            <div style={{ display: "flex" }}>
                              <strong>{cell.question}</strong>
                            </div>
                          </>
                        ) : colKey === "likes" ? (
                          <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {cell === 1 && <ThumbsUp color="green" size={20} />}
                          </td>
                        ) : colKey === "ip_address" ? (
                          <a
                            href={`https://whatismyipaddress.com/ip/${cell}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#007bff", textDecoration: "underline" }}
                          >
                            {cell}
                          </a>
                        ) : colKey === "answer" ? (
                                          <Readmore text={row.answer} apiEndpoint="/api/edit-answer" id={row.id} />
                                        ) : (
                                          cell
                                        )}
                            </DataTableBodyCell>
                          );
                        })}

                                  </TableRow>
                                ))}
            </TableBody>

            <SoftBox pb={1} display="flex" justifyContent="space-between" alignItems="center" paddingTop="10px" paddingLeft="19px">
              <SoftTypography variant="button" color="secondary">
                Showing {new Intl.NumberFormat(enUS).format(start)} to {new Intl.NumberFormat(enUS).format(end)} of {new Intl.NumberFormat(enUS).format(totalEntries)} entries
              </SoftTypography>
            </SoftBox>
          </Table>

          <SoftBox display="flex" justifyContent="space-between" alignItems="center"  p={1}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="circular"
              color="info"
              sx={{
                "& .MuiPaginationItem-root": {
                  "&.Mui-selected": {
                    background: "#219297",
                    color: "white !important", 
                    "&:hover": {
                      background: "teal",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    color: "black",
                  },
                },
              }}
            />
            <SoftTypography variant="button">Page {new Intl.NumberFormat(enUS).format(page)} of {new Intl.NumberFormat(enUS).format(totalPages)}</SoftTypography>
          </SoftBox>
        </>
      )}
    </TableContainer>
  );
}

export default DataTable;
